<template>
  <div class="bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden">
    <div class="flex items-center gap-2 mb-6">
      <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
      <h3 class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
        {{ t('app.views.cashRegister.paymentBreakdown') }}
      </h3>
      <div class="h-px bg-gray-200 dark:bg-gray-700 flex-1" />
    </div>
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
      <PaymentBreakdownCard
        v-for="method in paymentMethods"
        :key="method.key"
        :label="method.label"
        :value="formatCurrency(paymentBreakdown[method.key] || 0)"
        :icon-bg-class="method.iconBgClass"
        :icon-color-class="method.iconColorClass"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth';
import { formatCurrency } from '@/utils/priceHelpers';
import type { SessionSummary } from './sessionDetailsTypes';
import PaymentBreakdownCard from './PaymentBreakdownCard.vue';

interface Props {
  paymentBreakdown: SessionSummary['payment_breakdown'];
}

const props = defineProps<Props>();
const { t } = useI18n();
const authStore = useAuthStore();

// Standard payment methods configuration
const standardPaymentMethods = [
  { key: 'cash', label: t('app.views.cashRegister.cash'), iconBgClass: 'bg-green-100 dark:bg-green-900/30', iconColorClass: 'bg-green-500' },
  { key: 'card', label: t('app.views.cashRegister.card'), iconBgClass: 'bg-blue-100 dark:bg-blue-900/30', iconColorClass: 'bg-blue-500' },
  { key: 'digital', label: 'Digital', iconBgClass: 'bg-purple-100 dark:bg-purple-900/30', iconColorClass: 'bg-purple-500' },
  { key: 'other', label: t('app.views.cashRegister.other'), iconBgClass: 'bg-gray-100 dark:bg-gray-700', iconColorClass: 'bg-gray-500' }
];

// Generate unique colors for custom payment methods
const getCustomPaymentMethodColors = (methodKey: string) => {
  const colors = [
    { bg: 'bg-orange-100 dark:bg-orange-900/30', icon: 'bg-orange-500' },
    { bg: 'bg-pink-100 dark:bg-pink-900/30', icon: 'bg-pink-500' },
    { bg: 'bg-indigo-100 dark:bg-indigo-900/30', icon: 'bg-indigo-500' },
    { bg: 'bg-teal-100 dark:bg-teal-900/30', icon: 'bg-teal-500' },
    { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'bg-amber-500' },
    { bg: 'bg-lime-100 dark:bg-lime-900/30', icon: 'bg-lime-500' },
    { bg: 'bg-cyan-100 dark:bg-cyan-900/30', icon: 'bg-cyan-500' },
    { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'bg-emerald-500' }
  ];

  // Generate consistent color based on method name hash
  let hash = 0;
  for (let i = 0; i < methodKey.length; i++) {
    hash = methodKey.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % colors.length;

  return colors[colorIndex];
};

// Get all payment methods including custom ones
const paymentMethods = computed(() => {
  const methods = [...standardPaymentMethods];

  // Add custom methods from restaurant settings
  const customMethods = authStore.restaurant?.payment_methods_config?.custom_methods || [];
  customMethods.forEach((method: any) => {
    if (method.enabled) {
      const colors = getCustomPaymentMethodColors(method.name);
      methods.push({
        key: method.name,
        label: method.name,
        iconBgClass: colors.bg,
        iconColorClass: colors.icon
      });
    }
  });

  // Filter only methods that have values > 0
  return methods.filter(method => props.paymentBreakdown[method.key] > 0);
});
</script>
